import React, { useState, useRef, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ChevronDown, ChevronRight, Upload, Sparkles, HelpCircle,
  FolderOpen, FileText, X, CheckCircle, Filter, Leaf, Users, Shield,
  Loader2, Info, type LucideIcon,
} from "lucide-react";
import type { AlignmentOutletContext } from "../AlignmentModule";
import { type CriterionStatus, type CriterionState } from "../norms/normConfig";
import { useGetEsgIndicators, type EsgIndicator } from "../../../hooks/esg/useGetEsgIndicators";
import WorkInProgress from "../WorkInProgress";
import "./EvaluationPage.css";

/* ── Pillar config (same as EsgReferentielSection) ── */
type PillarCode = "E" | "S" | "G";

interface PillarConfig {
  code: PillarCode;
  label: string;
  color: string;
  lightBg: string;
  icon: LucideIcon;
}

const PILLARS: PillarConfig[] = [
  { code: "E", label: "Environnement", color: "#16a34a", lightBg: "#dcfce7", icon: Leaf },
  { code: "S", label: "Social",        color: "#d97706", lightBg: "#fef3c7", icon: Users },
  { code: "G", label: "Gouvernance",   color: "#475569", lightBg: "#f1f5f9", icon: Shield },
];

/* ── Status config ── */
const STATUS_OPTIONS: CriterionStatus[] = [
  "A verifier", "Conforme", "Partiel", "Non conforme", "Non applicable",
];
const STATUS_LABELS: Record<CriterionStatus, string> = {
  "A verifier":     "A vérifier",
  "Conforme":       "Conforme",
  "Partiel":        "Partiel",
  "Non conforme":   "Non conforme",
  "Non applicable": "Non applicable",
};
const STATUS_COLORS: Record<CriterionStatus, string> = {
  "A verifier":     "#e11d48",
  "Conforme":       "#16a34a",
  "Partiel":        "#d97706",
  "Non conforme":   "#dc2626",
  "Non applicable": "#9ca3af",
};

const DEFAULT_DOC_TYPES = [
  "Rapport annuel", "Politique RSE", "Certificat", "Données brutes",
  "Audit externe", "Procédure interne", "Autre",
];

const DEFAULT_STATE: CriterionState = {
  status: "A verifier",
  response: "",
  selectedDocType: DEFAULT_DOC_TYPES[0],
  uploadedFiles: [],
};

/* ── Status badge ── */
const StatusBadge: React.FC<{ status: CriterionStatus }> = ({ status }) => (
  <span
    className="ev-badge ev-badge--status"
    style={{
      background: `${STATUS_COLORS[status]}14`,
      color: STATUS_COLORS[status],
      borderColor: `${STATUS_COLORS[status]}28`,
    }}
  >
    {STATUS_LABELS[status]}
  </span>
);

/* ── Indicator panel (expanded) ── */
const IndicatorPanel: React.FC<{ indicator: EsgIndicator }> = ({ indicator }) => {
  const { criteriaStates, updateCriterionState } = useOutletContext<AlignmentOutletContext>();
  const key = String(indicator.id);
  const state = criteriaStates[key] ?? DEFAULT_STATE;
  const fileRef = useRef<HTMLInputElement>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const setStatus = (s: CriterionStatus) => updateCriterionState(key, { status: s });
  const setResponse = (r: string) => updateCriterionState(key, { response: r });
  const setDocType = (d: string) => updateCriterionState(key, { selectedDocType: d });

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const newFiles = Array.from(files).map((f) => ({ name: f.name, size: f.size }));
      updateCriterionState(key, {
        uploadedFiles: [...(state.uploadedFiles ?? []), ...newFiles],
      });
    },
    [key, state.uploadedFiles, updateCriterionState]
  );

  const removeFile = (idx: number) => {
    const next = state.uploadedFiles.filter((_, i) => i !== idx);
    updateCriterionState(key, { uploadedFiles: next });
  };

  const handlePrefill = () => {
    const gri = indicator.gri_standards.map((g) => g.code).join(", ");
    const text = `Indicateur : ${indicator.name}${gri ? ` (${gri})` : ""}.\n\nDécrivez ici vos pratiques, données et engagements associés à cet indicateur pour la période de reporting.`;
    setResponse(text);
    setPrefilled(true);
    setTimeout(() => setPrefilled(false), 2500);
  };

  const selectedDocType = state.selectedDocType || DEFAULT_DOC_TYPES[0];

  return (
    <div className="ev-panel">
      {indicator.remarks && (
        <p className="ev-panel-desc">{indicator.remarks}</p>
      )}

      {/* Status + actions */}
      <div className="ev-panel-controls">
        <div className="ev-select-wrap">
          <select
            className="ev-status-select"
            value={state.status}
            onChange={(e) => setStatus(e.target.value as CriterionStatus)}
            style={{ borderColor: `${STATUS_COLORS[state.status]}40` }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <ChevronDown size={13} className="ev-select-arrow" />
        </div>
        <button className="ev-btn ev-btn--prefill" onClick={handlePrefill}>
          <Sparkles size={14} />
          {prefilled ? "Pré-rempli !" : "Pré-remplir la réponse"}
        </button>
        <div className="ev-help-wrap">
          <button className="ev-btn ev-btn--help" onClick={() => setShowHelp((h) => !h)}>
            <HelpCircle size={14} />
            Aide contextuelle
          </button>
          {showHelp && (
            <div className="ev-help-bubble">
              <button className="ev-help-close" onClick={() => setShowHelp(false)}><X size={12} /></button>
              <p>
                {indicator.gri_standards.length > 0
                  ? `Cet indicateur est aligné avec ${indicator.gri_standards.map((g) => g.code).join(", ")}. Documentez vos données mesurables, politiques et engagements.`
                  : `Documentez vos pratiques, indicateurs et politiques associés à "${indicator.name}".`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Response */}
      <div className="ev-field">
        <label className="ev-field-label">Réponse / Description</label>
        <textarea
          className="ev-textarea"
          value={state.response}
          onChange={(e) => setResponse(e.target.value)}
          placeholder="Décrivez votre situation, vos engagements et vos pratiques opérationnelles."
          rows={4}
        />
      </div>

      {/* Document upload */}
      <div className="ev-field">
        <label className="ev-field-label">
          Documents justificatifs —{" "}
          <span className={state.uploadedFiles.length > 0 ? "ev-docs-done" : "ev-docs-remaining"}>
            {state.uploadedFiles.length} fichier(s) joint(s)
          </span>
        </label>

        <div className="ev-select-wrap ev-doctype-select">
          <select
            className="ev-select"
            value={selectedDocType}
            onChange={(e) => setDocType(e.target.value)}
          >
            {DEFAULT_DOC_TYPES.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <ChevronDown size={13} className="ev-select-arrow" />
        </div>

        <div
          className={`ev-drop-zone${dragOver ? " ev-drop-zone--over" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={20} color="#9ca3af" />
          <span className="ev-drop-text">Déposez vos fichiers ici</span>
          <span className="ev-drop-hint">Limite 50 Mo par fichier · PDF, DOCX, XLSX, CSV, TXT</span>
          <button
            type="button"
            className="ev-browse-btn"
            onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
          >
            Parcourir
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
            style={{ display: "none" }}
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        {state.uploadedFiles.length > 0 && (
          <div className="ev-file-list">
            {state.uploadedFiles.map((f, i) => (
              <div key={i} className="ev-file-item">
                <FileText size={14} color="#5b3fa6" />
                <span className="ev-file-name">{f.name}</span>
                <span className="ev-file-size">({Math.round(f.size / 1024)} KB)</span>
                <button className="ev-file-remove" onClick={() => removeFile(i)}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ev-panel-actions">
        <button className="ev-btn ev-btn--dossier">
          <FolderOpen size={14} />
          Associer un dossier
        </button>
        <button className="ev-btn ev-btn--template">
          <Sparkles size={14} />
          Générer un modèle
        </button>
      </div>
    </div>
  );
};

/* ── Indicator row ── */
const IndicatorRow: React.FC<{
  indicator: EsgIndicator;
  expanded: boolean;
  onToggle: () => void;
  pillarColor: string;
}> = ({ indicator, expanded, onToggle, pillarColor }) => {
  const { criteriaStates } = useOutletContext<AlignmentOutletContext>();
  const state = criteriaStates[String(indicator.id)] ?? DEFAULT_STATE;

  return (
    <div className={`ev-criterion${expanded ? " ev-criterion--open" : ""}`}>
      <button className="ev-criterion-header" onClick={onToggle}>
        <span className="ev-criterion-toggle">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className="ev-criterion-code" style={{ color: pillarColor }}>
          #{indicator.id}
        </span>
        <span className="ev-criterion-dash">—</span>
        <span className="ev-criterion-name">{indicator.name}</span>
        <div className="ev-criterion-badges">
          {indicator.gri_standards.length > 0 && (
            <span className="ev-gri-chip">
              {indicator.gri_standards.map((g) => g.code).join(", ")}
            </span>
          )}
          <span className={`ev-type-chip ev-type-chip--${indicator.measure_type}`}>
            {indicator.measure_type === "quantitative" ? "Quant." : "Qual."}
          </span>
          <StatusBadge status={state.status} />
          {state.status === "Conforme" && <CheckCircle size={14} color="#16a34a" />}
        </div>
        {!expanded && state.uploadedFiles.length > 0 && (
          <span className="ev-criterion-docs-hint">
            {state.uploadedFiles.length} doc(s)
          </span>
        )}
      </button>
      {expanded && <IndicatorPanel indicator={indicator} />}
    </div>
  );
};

/* ── Main EvaluationPage ── */
const EvaluationPage: React.FC = () => {
  const { selectedNorm, criteriaStates } = useOutletContext<AlignmentOutletContext>();
  const [activePillar, setActivePillar] = useState<PillarCode>("E");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("Tous");
  const [filterType, setFilterType] = useState<string>("Tous");

  const { data: indicators = [], isLoading, isError } = useGetEsgIndicators();

  const grouped = useMemo(() => {
    const result: Record<string, Record<string, EsgIndicator[]>> = {};
    indicators.forEach((ind) => {
      const p = ind.theme.pillar.code;
      const t = ind.theme.name;
      if (!result[p]) result[p] = {};
      if (!result[p][t]) result[p][t] = [];
      result[p][t].push(ind);
    });
    return result;
  }, [indicators]);

  const activePillarConfig = PILLARS.find((p) => p.code === activePillar)!;

  const themes = useMemo(() => grouped[activePillar] ?? {}, [grouped, activePillar]);

  const filteredThemes = useMemo(() => {
    const result: Record<string, EsgIndicator[]> = {};
    Object.entries(themes).forEach(([theme, inds]) => {
      const filtered = inds.filter((ind) => {
        const typeOk = filterType === "Tous"
          || (filterType === "Quantitatif" && ind.measure_type === "quantitative")
          || (filterType === "Qualitatif" && ind.measure_type === "qualitative");
        const status = criteriaStates[String(ind.id)]?.status ?? "A verifier";
        const statusOk = filterStatus === "Tous" || status === filterStatus;
        return typeOk && statusOk;
      });
      if (filtered.length > 0) result[theme] = filtered;
    });
    return result;
  }, [themes, filterType, filterStatus, criteriaStates]);

  const totalInPillar = useMemo(() => Object.values(themes).flat().length, [themes]);
  const conformInPillar = useMemo(
    () => Object.values(themes).flat().filter((ind) => criteriaStates[String(ind.id)]?.status === "Conforme").length,
    [themes, criteriaStates]
  );

  const toggle = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  if (selectedNorm !== "GRI") return <WorkInProgress normLabel={selectedNorm} />;

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <h1 className="ev-page-title">Évaluation GRI</h1>
        <p className="ev-page-desc">
          Évaluez votre conformité aux Standards GRI — répondez à chaque indicateur et uploadez vos preuves documentaires.
        </p>
      </div>

      {/* Filters */}
      <div className="ev-filters-row">
        <div className="ev-filter-group">
          <Filter size={14} color="#6b7280" />
          <label className="ev-filter-label">Type</label>
          <div className="ev-select-wrap ev-filter-select">
            <select className="ev-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option>Tous</option>
              <option>Quantitatif</option>
              <option>Qualitatif</option>
            </select>
            <ChevronDown size={12} className="ev-select-arrow" />
          </div>
        </div>
        <div className="ev-filter-group">
          <label className="ev-filter-label">Statut</label>
          <div className="ev-select-wrap ev-filter-select">
            <select className="ev-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option>Tous</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            <ChevronDown size={12} className="ev-select-arrow" />
          </div>
        </div>
      </div>

      {/* Pillar tabs */}
      <div className="ev-pillar-tabs">
        {PILLARS.map((p) => {
          const isActive = activePillar === p.code;
          const inds = Object.values(grouped[p.code] ?? {}).flat();
          const Icon = p.icon;
          return (
            <button
              key={p.code}
              className={`ev-pillar-tab${isActive ? " ev-pillar-tab--active" : ""}`}
              style={isActive ? { backgroundColor: p.color, borderColor: p.color, color: "#fff" } : {}}
              onClick={() => { setActivePillar(p.code); setExpandedId(null); }}
            >
              <span
                className="ev-pillar-tab-icon"
                style={isActive
                  ? { background: "rgba(255,255,255,0.2)", color: "#fff" }
                  : { background: p.lightBg, color: p.color }}
              >
                <Icon size={15} strokeWidth={2.2} />
              </span>
              <span>{p.label}</span>
              <span
                className="ev-pillar-tab-count"
                style={isActive ? { background: "rgba(255,255,255,0.2)", color: "#fff" } : {}}
              >
                {inds.length > 0
                  ? `${Object.values(grouped[p.code] ?? {}).flat().filter((i) => criteriaStates[String(i.id)]?.status === "Conforme").length}/${inds.length}`
                  : "—"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Pillar summary strip */}
      {!isLoading && !isError && totalInPillar > 0 && (
        <div
          className="ev-pillar-summary"
          style={{ borderLeftColor: activePillarConfig.color, background: `${activePillarConfig.lightBg}88` }}
        >
          <span className="ev-summary-pillar" style={{ color: activePillarConfig.color }}>
            <activePillarConfig.icon size={15} strokeWidth={2.2} />
            {activePillarConfig.label}
          </span>
          <span className="ev-summary-divider" />
          <span className="ev-summary-stat">
            <strong style={{ color: activePillarConfig.color }}>{totalInPillar}</strong>
            <span>indicateurs</span>
          </span>
          <span className="ev-summary-stat">
            <strong style={{ color: "#16a34a" }}>{conformInPillar}</strong>
            <span>conformes</span>
          </span>
          <span className="ev-summary-stat">
            <strong style={{ color: activePillarConfig.color }}>{Object.keys(themes).length}</strong>
            <span>thèmes</span>
          </span>
        </div>
      )}

      {/* States */}
      {isLoading && (
        <div className="ev-state-msg">
          <Loader2 size={18} className="ev-spinner" />
          Chargement des indicateurs…
        </div>
      )}
      {isError && (
        <div className="ev-state-msg ev-state-error">
          <Info size={16} />
          Impossible de charger les indicateurs. Vérifiez la connexion au backend.
        </div>
      )}

      {/* Theme groups + indicator rows */}
      {!isLoading && !isError && (
        <div className="ev-criteria-list">
          {Object.keys(filteredThemes).length === 0 && (
            <div className="ev-empty">Aucun indicateur ne correspond aux filtres sélectionnés.</div>
          )}
          {Object.entries(filteredThemes).map(([themeName, inds]) => (
            <div key={themeName} className="ev-theme-group">
              <div className="ev-theme-group-title" style={{ borderLeftColor: activePillarConfig.color }}>
                {themeName}
                <span className="ev-theme-group-count">{inds.length}</span>
              </div>
              {inds.map((ind) => (
                <IndicatorRow
                  key={ind.id}
                  indicator={ind}
                  expanded={expandedId === ind.id}
                  onToggle={() => toggle(ind.id)}
                  pillarColor={activePillarConfig.color}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EvaluationPage;
